import { decode } from 'html-entities';

/**
 * Shape of a single organizer inside the follow-status READ response
 * (`GET /api/v3/organizers/?ids=&expand.organizer=follow_status`). snake_case,
 * exactly as Core returns it.
 */
export interface RawOrganizer {
    id: string;
    name: string;
    url: string;
    logo?: { url?: string } | null;
    num_future_events?: number;
    follow_status?: {
        followed_by_you?: boolean;
        num_followers?: number;
    };
}

/**
 * Normalized (camelCase) organizer used throughout the UI + follow-state layer.
 */
export interface OrganizerData {
    id: string;
    name: string;
    url: string;
    profilePicture?: string;
    followedByYou?: boolean;
    numFollowers?: number;
    numUpcomingEvents?: number;
    /** Optional caller-supplied context line, takes precedence over the counts. */
    customDataPoint?: string;
}

export type OrganizerDataMap = Record<string, OrganizerData>;

/**
 * Port of eb-ui `src/api/transformations.js::normalizeOrganizers`. Maps the raw
 * Core organizers (snake_case, nested `follow_status`) to the flat camelCase
 * `OrganizerData` map keyed by id. `name` is html-entity decoded exactly as the
 * original did (Core returns HTML-escaped names).
 */
export const normalizeOrganizers = (
    organizers: RawOrganizer[],
): OrganizerDataMap =>
    organizers.reduce<OrganizerDataMap>((acc, org) => {
        const {
            id,
            name,
            url,
            logo,
            num_future_events: numUpcomingEvents,
            follow_status: {
                followed_by_you: followedByYou,
                num_followers: numFollowers,
            } = {},
        } = org;

        const profilePicture = logo ? logo.url : undefined;

        return {
            [id]: {
                id,
                name: decode(name),
                url,
                profilePicture,
                followedByYou,
                numFollowers,
                numUpcomingEvents,
            },
            ...acc,
        };
    }, {});
